import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  Download,
  Trash2,
  Pencil,
  FileText,
  Loader2,
  Plus,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { userDocumentService } from '@/services/userDocumentService';
import type { UserDocument, UpdateUserDocumentInput } from '@/types/userDocument';
import { getExpiryStatus, getExpiryLabel } from '@/types/userDocument';

interface UserDocumentsProps {
  userId: string;
  disabled?: boolean;
}

const NEW_TYPE_VALUE = '__new__';

export function UserDocuments({ userId, disabled }: UserDocumentsProps) {
  const [documents, setDocuments] = useState<UserDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [usedTypes, setUsedTypes] = useState<string[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState<UserDocument | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Upload form state
  const [uploadName, setUploadName] = useState('');
  const [uploadType, setUploadType] = useState('');
  const [uploadExpiry, setUploadExpiry] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadShowCustomType, setUploadShowCustomType] = useState(false);
  const [uploadCustomType, setUploadCustomType] = useState('');

  // Edit form state
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState('');
  const [editExpiry, setEditExpiry] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editShowCustomType, setEditShowCustomType] = useState(false);
  const [editCustomType, setEditCustomType] = useState('');

  const loadDocuments = useCallback(async () => {
    if (!userId) return;
    try {
      const [data, types] = await Promise.all([
        userDocumentService.getDocuments(userId),
        userDocumentService.getUsedDocumentTypes(),
      ]);
      setDocuments(data);
      setUsedTypes(types);
    } catch (err) {
      console.error('Error loading user documents:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const resetUploadForm = () => {
    setUploadName('');
    setUploadType('');
    setUploadExpiry('');
    setUploadNotes('');
    setUploadFile(null);
    setUploadShowCustomType(false);
    setUploadCustomType('');
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName.trim()) return;

    setUploading(true);
    try {
      await userDocumentService.uploadDocument({
        user_id: userId,
        document_name: uploadName.trim(),
        document_type: uploadType || undefined,
        expires_at: uploadExpiry || null,
        notes: uploadNotes || undefined,
        file: uploadFile,
      });
      toast.success('Document uploaded');
      setShowUploadDialog(false);
      resetUploadForm();
      loadDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (doc: UserDocument) => {
    setEditingDoc(doc);
    setEditName(doc.document_name);
    setEditType(doc.document_type || '');
    setEditExpiry(doc.expires_at || '');
    setEditNotes(doc.notes || '');
    setEditShowCustomType(false);
    setEditCustomType('');
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDoc || !editName.trim()) return;

    setSaving(true);
    try {
      const updates: UpdateUserDocumentInput = {
        document_name: editName.trim(),
        document_type: editType || null,
        expires_at: editExpiry || null,
        notes: editNotes || null,
      };
      await userDocumentService.updateDocument(editingDoc.id, updates);
      toast.success('Document updated');
      setShowEditDialog(false);
      setEditingDoc(null);
      loadDocuments();
    } catch (err) {
      console.error('Error updating document:', err);
      toast.error('Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc: UserDocument) => {
    setDeletingId(doc.id);
    try {
      await userDocumentService.deleteDocument(doc);
      toast.success('Document deleted');
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Failed to delete document');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (doc: UserDocument) => {
    try {
      const url = await userDocumentService.getDownloadUrl(doc.storage_path);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading document:', err);
      toast.error('Failed to download document');
    }
  };

  const expiryBadge = (doc: UserDocument) => {
    const status = getExpiryStatus(doc.expires_at);
    const label = getExpiryLabel(status, doc.expires_at);

    switch (status) {
      case 'expired':
        return <Badge variant="destructive" className="text-xs">{label}</Badge>;
      case 'expiring_soon':
        return <Badge className="text-xs bg-yellow-500/15 text-yellow-700 border-yellow-300">{label}</Badge>;
      case 'valid':
        return <Badge className="text-xs bg-green-500/15 text-green-700 border-green-300">{label}</Badge>;
      default:
        return <span className="text-xs text-muted-foreground">—</span>;
    }
  };

  // Document type selector — dropdown of previously used types + option to add new
  const DocumentTypeField = ({
    value,
    onChange,
    showCustomInput,
    setShowCustomInput,
    customValue,
    setCustomValue,
  }: {
    value: string;
    onChange: (v: string) => void;
    showCustomInput: boolean;
    setShowCustomInput: (v: boolean) => void;
    customValue: string;
    setCustomValue: (v: string) => void;
  }) => (
    <div className="space-y-1.5">
      <Label>Document Type</Label>
      {showCustomInput ? (
        <div className="flex gap-2">
          <Input
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder="Enter new document type..."
            className="text-sm"
            autoFocus
          />
          <Button
            type="button"
            size="sm"
            className="h-9 shrink-0"
            disabled={!customValue.trim()}
            onClick={() => {
              onChange(customValue.trim());
              setShowCustomInput(false);
            }}
          >
            Add
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-9 shrink-0"
            onClick={() => {
              setShowCustomInput(false);
              setCustomValue('');
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Select
          value={value || undefined}
          onValueChange={(v) => {
            if (v === NEW_TYPE_VALUE) {
              setShowCustomInput(true);
              setCustomValue('');
            } else {
              onChange(v);
            }
          }}
        >
          <SelectTrigger className="text-sm">
            <SelectValue placeholder="Select or add a type..." />
          </SelectTrigger>
          <SelectContent>
            {usedTypes.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
            {usedTypes.length > 0 && (
              <div className="border-t my-1" />
            )}
            <SelectItem value={NEW_TYPE_VALUE}>
              <span className="flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Add new type...
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="border border-border rounded-lg p-4">
        <h3 className="text-sm font-medium text-foreground mb-4">Documents</h3>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-border rounded-lg p-4"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-foreground">Documents</h3>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setShowUploadDialog(true)}
          disabled={disabled}
          className="h-7 text-xs"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Document
        </Button>
      </div>

      {documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No documents uploaded</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Upload insurance certificates, licenses, and other contractor documents.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Name</TableHead>
              <TableHead className="text-xs">Type</TableHead>
              <TableHead className="text-xs">Expires</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="truncate">{doc.document_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {doc.file_name}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {doc.document_type || '—'}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {doc.expires_at
                    ? format(new Date(doc.expires_at), 'MMM d, yyyy')
                    : '—'}
                </TableCell>
                <TableCell>{expiryBadge(doc)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => handleDownload(doc)}
                      title="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => handleEdit(doc)}
                      disabled={disabled}
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(doc)}
                      disabled={disabled || deletingId === doc.id}
                      title="Delete"
                    >
                      {deletingId === doc.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Expiry warnings summary */}
      {documents.some(d => {
        const s = getExpiryStatus(d.expires_at);
        return s === 'expired' || s === 'expiring_soon';
      }) && (
        <div className="mt-3 flex items-start gap-2 p-2.5 rounded-md bg-yellow-500/10 border border-yellow-300/50">
          <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-yellow-700">
            {documents.filter(d => getExpiryStatus(d.expires_at) === 'expired').length > 0 && (
              <span className="font-medium">
                {documents.filter(d => getExpiryStatus(d.expires_at) === 'expired').length} expired
              </span>
            )}
            {documents.filter(d => getExpiryStatus(d.expires_at) === 'expired').length > 0 &&
              documents.filter(d => getExpiryStatus(d.expires_at) === 'expiring_soon').length > 0 && ' · '}
            {documents.filter(d => getExpiryStatus(d.expires_at) === 'expiring_soon').length > 0 && (
              <span>
                {documents.filter(d => getExpiryStatus(d.expires_at) === 'expiring_soon').length} expiring soon
              </span>
            )}
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for this user. Supported: PDF, images, and common document formats.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Document Name *</Label>
              <Input
                value={uploadName}
                onChange={(e) => setUploadName(e.target.value)}
                placeholder="e.g. General Liability Insurance 2026"
              />
            </div>

            <DocumentTypeField
              value={uploadType}
              onChange={setUploadType}
              showCustomInput={uploadShowCustomType}
              setShowCustomInput={setUploadShowCustomType}
              customValue={uploadCustomType}
              setCustomValue={setUploadCustomType}
            />

            <div className="space-y-1.5">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={uploadExpiry}
                onChange={(e) => setUploadExpiry(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Optional notes..."
              />
            </div>

            <div className="space-y-1.5">
              <Label>File *</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate max-w-[200px]">{uploadFile.name}</span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-5 w-5"
                      onClick={() => setUploadFile(null)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-1" />
                    <p className="text-sm text-muted-foreground">Click to select a file</p>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                      onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowUploadDialog(false); resetUploadForm(); }}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !uploadFile || !uploadName.trim()}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Document</DialogTitle>
            <DialogDescription>
              Update the document details. The file itself cannot be changed — delete and re-upload if needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Document Name *</Label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>

            <DocumentTypeField
              value={editType}
              onChange={setEditType}
              showCustomInput={editShowCustomType}
              setShowCustomInput={setEditShowCustomType}
              customValue={editCustomType}
              setCustomValue={setEditCustomType}
            />

            <div className="space-y-1.5">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={editExpiry}
                onChange={(e) => setEditExpiry(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveEdit}
              disabled={saving || !editName.trim()}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
